import React, { useEffect, useRef, useState } from 'react';
import { Card, Table, Pagination } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus, faPlus, faExchangeAlt } from '@fortawesome/free-solid-svg-icons';
import { useLocation, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import axios from 'axios';
import Swal from 'sweetalert2';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Papa from 'papaparse';
import { API_URL, END_POINT_QUOTES, END_POINT_ADMIN } from '../const';
import { hasUserPermission, isVendorOwner, isSuperAdmin, readSessionAdmin } from '../utils/userPermissions';
import { cacheQuoteDetail, clearQuoteDataCache, getQuoteDetail, forgetQuoteDetail, isCurrentQuoteSession, quoteQueryParams, requestQuotes } from '../utils/quoteRequests';
import useQuoteListing, { useQuoteDetail, useQuoteOptions } from '../hooks/useQuoteListing';
import { getSelectStyles } from './SearchableSelect';
import { QuoteDetailSidebar } from './QuoteDetailSidebar';
import QuoteTransferModal from './QuoteTransferModal';
import DatePickerField from './DatePickerField';

const nestedValue = (object, path) => path.split('.').reduce((value, key) => value?.[key], object);
const columns = [
  // { label: 'SR No.', key: 'sr_no', width: '8%' },
  { label: 'Offer No.', key: 'offer_no', width: '12%' },
  { label: 'Customer', key: 'customer.name', width: '16%' },
  { label: 'Company', key: 'company.name', width: '12%' },
  { label: 'Sales Executive', key: 'sales_executive.name', width: '13%' },
  { label: 'Created By', key: 'owner.name', width: '13%', sortable: false },
  { label: 'Total', key: 'total', width: '10%' },
  { label: 'Created Date', key: 'created_at', width: '12%' },
  { label: 'Expected Date', key: 'expected_date', width: '12%' },
  { label: 'Status', key: 'status', width: '9%' },
];
const exportColumns = [
  ['ID', 'id'], ['SR No.', 'sr_no'], ['Offer No.', 'offer_no'],
  ['System Quote No.', 'system_quote_no'], ['Offer Date', 'offer_date'],
  ['Customer', 'customer.name'], ['Place', 'place'], ['Company', 'company.name'],
  ['Branch', 'branch.name'], ['Principal', 'principal.name'], ['Product Code', 'product_code'],
  ['Category', 'category.name'], ['Quantity', 'qty'], ['Total', 'total'],
  ['Sales Executive', 'sales_executive.name'], ['Expected Date', 'expected_date'],
  ['Status', 'status'], ['Remarks', 'remarks'],
];
const ErrorNotice = ({ message, retry }) => message ? (
  <div className="alert alert-danger mx-3" role="alert">
    {message} <button type="button" className="btn btn-sm btn-outline-danger ms-2" onClick={retry}>Retry</button>
  </div>
) : null;

const saveExport = (rows, format) => {
  const values = rows.map(row => exportColumns.map(([, key]) => {
    const value = nestedValue(row, key);
    return typeof value === 'string' || typeof value === 'number' ? value : '';
  }));
  if (format === 'pdf') {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'A4' });
    autoTable(doc, {
      head: [exportColumns.map(([label]) => label)], body: values, startY: 20,
      styles: { fontSize: 7, cellPadding: 4 },
      headStyles: { fillColor: [41, 128, 185], textColor: 255, halign: 'center' },
      didDrawPage: data => { doc.setFontSize(10); doc.text('Quote List', data.settings.margin.left, 10); },
    });
    doc.save('quotes.pdf');
    return;
  }
  const csv = Papa.unparse(values.map(row => Object.fromEntries(exportColumns.map(([label], index) => [label, row[index]]))));
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = 'quotes.csv';
  document.body.appendChild(link);
  try { link.click(); } finally { link.remove(); URL.revokeObjectURL(url); }
};

export default function QuoteListingTable({ compact = false } = {}) {
  const token = sessionStorage.getItem('quotetoken')?.replace(/^"|"$/g, '');
  const admin = readSessionAdmin();
  const navigate = useNavigate();
  const location = useLocation();
  const listing = useQuoteListing(token);
  const { rows, pagination, loading, error, filters, setFilters, query, setPage, sortBy } = listing;
  const [showFilters, setShowFilters] = useState(false);
  const [filtersRequested, setFiltersRequested] = useState(false);
  const options = useQuoteOptions(token, filtersRequested);
  const [selected, setSelected] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [checkedIds, setCheckedIds] = useState([]);
  const [showTransfer, setShowTransfer] = useState(false);
  const [teammates, setTeammates] = useState([]);
  const canTransferQuotes = !compact && (isVendorOwner(admin) || isSuperAdmin(admin));
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const detail = useQuoteDetail(token, selected?.sessionToken === token ? selected : null, showDetail);
  const [exportFormat, setExportFormat] = useState('');
  const [exportState, setExportState] = useState({ loading: false, error: '' });
  const exportRequest = useRef(null);
  const exportVersion = useRef(0);
  const queryIdentity = JSON.stringify([token, query.filters, query.sort]);

  useEffect(() => {
    setShowFilters(false);
    setFiltersRequested(false);
    clearQuoteDataCache();
    return () => clearQuoteDataCache();
  }, [token]);

  const selectedIndexForPrefetch = rows.findIndex(row => row.id === selected?.id);
  const previousId = rows[selectedIndexForPrefetch - 1]?.id;
  const nextId = rows[selectedIndexForPrefetch + 1]?.id;
  useEffect(() => {
    if (!showDetail || !selected || selected.sessionToken !== token) return undefined;
    let active = true;
    const requests = [previousId, nextId].filter(id => id && !getQuoteDetail(token, id)).map(id => {
      const request = requestQuotes(token, '/' + id);
      request.promise.then(payload => {
        if (active && isCurrentQuoteSession(token)) cacheQuoteDetail(token, payload.data);
      }).catch(() => {}); // A failed prefetch is retried when that quote is actually opened.
      return request;
    });
    return () => { active = false; requests.forEach(request => request.release()); };
  }, [token, showDetail, selected, previousId, nextId]);

  useEffect(() => {
    setShowDetail(false);
    setSelected(null);
    setPendingNavigation(null);
    setExportState({ loading: false, error: '' });
    return () => {
      exportVersion.current += 1;
      exportRequest.current?.release();
      exportRequest.current = null;
    };
  }, [queryIdentity]);

  // Arriving from a PO Sales "Quote No." link: open that quote's detail panel automatically.
  const openQuoteId = location.state?.openQuoteId;
  useEffect(() => {
    if (!openQuoteId || !token) return;
    setSelected({ id: openQuoteId, sessionToken: token });
    setShowDetail(true);
  }, [openQuoteId, token, location.key]);

  useEffect(() => {
    setCheckedIds(current =>current.filter(id => rows.some(row => row.id === id)));
  }, [rows]);

  useEffect(() => {
    if (!canTransferQuotes || !token) return undefined;
    let active = true;
    axios.get(API_URL + END_POINT_ADMIN, {
      headers: { Accept: 'application/json', Authorization: 'Bearer ' + token },
    }).then(response => {
      if (active && Array.isArray(response.data.data)) {
        setTeammates(response.data.data.filter(user => !isSuperAdmin(user)));
      }
    }).catch(() => { if (active) setTeammates([]); });
    return () => { active = false; };
  }, [canTransferQuotes, token]);

  const currentPage = pagination.current_page;
  const totalPages = Math.max(1, pagination.last_page);
  const offset = (currentPage - 1) * pagination.per_page;
  const busy = loading || query.page !== currentPage;
  const selectedIndex = rows.findIndex(row => row.id === selected?.id);
  const pageNumbers = totalPages <= 7
    ? Array.from({ length: totalPages }, (_, index) => index + 1)
    : [...new Set([1, currentPage - 1, currentPage, currentPage + 1, totalPages])]
      .filter(page => page >= 1 && page <= totalPages).sort((a, b) => a - b);

  useEffect(() => {
    if (!pendingNavigation || busy || error) return;
    const row = rows[pendingNavigation === 'first' ? 0 : rows.length - 1];
    setSelected(row ? { ...row, sessionToken: token } : null);
    setPendingNavigation(null);
  }, [pendingNavigation, busy, rows, error, token]);

  const openQuote = quote => { setSelected({ ...quote, sessionToken: token }); setShowDetail(true); };
  const closeDetail = () => { setShowDetail(false); setPendingNavigation(null); };
  const changePage = page => {
    if (busy || page === currentPage || page < 1 || page > totalPages) return;
    closeDetail();
    setSelected(null);
    setPage(page);
  };
  const navigateQuote = direction => {
    if (busy || pendingNavigation || selectedIndex < 0) return;
    const next = selectedIndex + direction;
    if (rows[next]) setSelected({ ...rows[next], sessionToken: token });
    else if (currentPage + direction >= 1 && currentPage + direction <= totalPages) {
      setPendingNavigation(direction > 0 ? 'first' : 'last');
      setPage(currentPage + direction);
    }
  };

  const handleDelete = async id => {
    const confirmation = await Swal.fire({
      title: 'Are you sure?', text: 'You will not be able to recover this quote', icon: 'warning',
      showCancelButton: true, confirmButtonText: 'Yes, delete it!', cancelButtonText: 'Cancel',
    });
    if (!confirmation.isConfirmed) return;
    Swal.fire({
      title: 'Deleting...', text: 'Please wait while we delete the quote.',
      allowOutsideClick: false, didOpen: () => Swal.showLoading(),
    });
    try {
      await axios.delete(API_URL + END_POINT_QUOTES + '/' + id, {
        headers: { Accept: 'application/json', Authorization: 'Bearer ' + token },
      });
      forgetQuoteDetail(token, id);
      closeDetail();
      setSelected(null);
      listing.refreshAfterDelete();
      Swal.fire({ icon: 'success', title: 'Deleted!', text: 'Quote has been deleted successfully.' });
    } catch (failure) {
      Swal.fire({ icon: 'error', title: 'Failed!', text: failure.response?.data?.message || 'Failed to delete quote.' });
    }
  };

  const handleExport = async () => {
    if (!exportFormat || exportState.loading || listing.filtersPending) return;
    const version = ++exportVersion.current;
    setExportState({ loading: true, error: '' });
    const request = requestQuotes(token, '', quoteQueryParams(query, 'export'));
    exportRequest.current = request;
    try {
      const payload = await request.promise;
      if (version !== exportVersion.current || !isCurrentQuoteSession(token)) return;
      saveExport(payload.data || [], exportFormat);
      setExportState({ loading: false, error: '' });
    } catch (failure) {
      if (version === exportVersion.current && isCurrentQuoteSession(token)) {
        setExportState({ loading: false, error: failure.response?.data?.message || failure.message || 'Unable to export quotes.' });
      }
    } finally {
      request.release();
      if (exportRequest.current === request) exportRequest.current = null;
    }
  };
  const updateFilter = (name, value) => setFilters(previous => ({ ...previous, [name]: value }));
  const toggleChecked = (event, id) => {
    event.stopPropagation();
    setCheckedIds(current => current.includes(id)
      ? current.filter(value => value !== id)
      : [...current, id]);
  };
  const openTransfer = () => {
    const selectedRows = rows.filter(row => checkedIds.includes(row.id));
    const owners = [...new Set(selectedRows.map(row => String(row.created_by_id)))];
    if (selectedRows.length === 0) return;
    if (owners.length !== 1) {
      Swal.fire({ icon: 'error', title: 'Cannot transfer', text: 'Select quotes that currently belong to the same user.' });
      return;
    }
    setShowTransfer(true);
  };
  const checkedQuotes = rows.filter(row => checkedIds.includes(row.id));
  const namedOptions = key => (options.data[key] || []).map(item => ({ value: item.name, label: item.name }));
  const filterConfig = [
    // ['SR No.', 'sr_no'], ['Offer No.', 'offer_no'], ['System Quote No.', 'system_quote_no'],
    ['Offer No.', 'offer_no'], ['System Quote No.', 'system_quote_no'],
    ['Offer Date (Start)', 'offer_date_start', null, 'date'],
    ['Offer Date (End)', 'offer_date_end', null, 'date'],
    ['Customer', 'customer.name', namedOptions('customers')], ['Place', 'place'],
    ['Company', 'company.name', namedOptions('companies')], ['Branch', 'branch.name', namedOptions('branches')],
    ['Principal', 'principal.name', namedOptions('principals')], ['Product Code', 'product_code'],
    ['Category', 'category.name', namedOptions('categories')], ['Quantity', 'qty'], ['Total', 'total'],
    ['User', 'sales_executive.name', namedOptions('admins')],
    ['Expected Date (Start)', 'expected_date_start', null, 'date'],
    ['Expected Date (End)', 'expected_date_end', null, 'date'],
    ['Status', 'status', ['Closed', 'Live', 'Lost', 'Won'].map(value => ({ value, label: value }))],
  ];
  const sortArrow = key => query.sort.key === key ? (query.sort.direction === 'asc' ? ' \u25b2' : ' \u25bc') : '';
  const visibleRows = compact ? rows.slice(0, 5) : rows;
  const pageIds = visibleRows.map(quote => quote.id);
  const allPageChecked = pageIds.length > 0 && pageIds.every(id => checkedIds.includes(id));

  return (
    <Card border="light" className={`overflow-hidden border-radius-5 mb-4${compact ? " dashboard-panel" : ""}`}>
      <Card.Body className="pb-0 p-0">
        {!compact && <div className="px-3 pt-3 mb-4">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="mb-0 font-20 text-quote-dark">Filters</h5>
            <button type="button" className="btn-quote-blue btn btn-primary py-2 px-2"
              aria-label={showFilters ? 'Hide filters' : 'Show filters'}
              onClick={() => { setShowFilters(previous => !previous); setFiltersRequested(true); }}>
              <FontAwesomeIcon icon={showFilters ? faMinus : faPlus} />
            </button>
          </div>
          {showFilters && <>
            <div className="d-flex justify-content-end align-items-center px-3 pb-3">
              <select aria-label="Export format" className="form-select me-2" style={{ width: 'auto' }}
                value={exportFormat} onChange={event => setExportFormat(event.target.value)}>
                <option value="">Select Format</option><option value="pdf">PDF</option><option value="csv">CSV</option>
              </select>
              <button type="button" className="btn btn-success" onClick={handleExport}
                disabled={!exportFormat || exportState.loading || listing.filtersPending}>
                {exportState.loading ? 'Exporting...' : 'Export'}
              </button>
            </div>
            <ErrorNotice message={exportState.error} retry={handleExport} />
            {options.loading && <div role="status">Loading filter options...</div>}
            <ErrorNotice message={options.error} retry={options.retry} />
            <div className="row g-3 border-top mt-3">
              {filterConfig.map(([label, name, choices, type]) => (
                <div className="col-md-3" key={name}>
                  <label htmlFor={'quote-filter-' + name}>{label}</label>
                  {choices ? <Select inputId={'quote-filter-' + name} isMulti options={choices}
                    value={choices.filter(option => (filters[name] || '').split(',').includes(option.value))}
                    onChange={selectedOptions => updateFilter(name, (selectedOptions || []).map(option => option.value).join(','))}
                    menuPortalTarget={document.body} styles={getSelectStyles()} />
                    : type === 'date' ? (
                      <DatePickerField
                        id={'quote-filter-' + name}
                        name={name}
                        value={filters[name] || ''}
                        onChange={value => updateFilter(name, value)}
                      />
                    ) : <input id={'quote-filter-' + name} type="text" name={name}
                      value={filters[name] || ''} onChange={event => updateFilter(name, event.target.value)}
                      className="form-control" />}
                </div>
              ))}
            </div>
          </>}
        </div>}
        {canTransferQuotes && checkedIds.length > 0 && <div className="px-3 pb-3 d-flex align-items-center gap-3">
          <span>{checkedIds.length} selected</span>
          <button type="button" className="btn btn-outline-primary btn-sm" onClick={openTransfer}>
            <FontAwesomeIcon icon={faExchangeAlt} className="me-2" />Transfer Quotes
          </button>
        </div>}
        <ErrorNotice message={error} retry={listing.retry} />
        {loading && <div className="p-3 text-center" role="status">{rows.length ? 'Updating quotes...' : 'Processing...'}</div>}
        {!loading && !error && rows.length === 0 && <div className="listing-empty-state" role="status">No Quotes Found</div>}
        {rows.length > 0 && <Table responsive aria-busy={busy} className="table-centered quotes-list-table rounded mb-0">
          <caption className="d-lg-none quote-sort-caption">Select a column heading to sort</caption>
          <colgroup>
            {canTransferQuotes && <col style={{ width: '4%' }} />}
            <col style={{ width: '6%' }} />
            {columns.map(column => <col key={column.key} style={{ width: column.width }} />)}
          </colgroup>
          <thead className="thead-light"><tr>
            {canTransferQuotes && <th className="border-0">
              <input type="checkbox" aria-label="Select all quotes on this page"
                checked={allPageChecked}
                onChange={event => setCheckedIds(event.target.checked
                  ? [...new Set([...checkedIds, ...pageIds])]
                  : checkedIds.filter(id => !pageIds.includes(id)))} />
            </th>}
            {[{ label: '#', key: 'index' }, ...columns].map(({ label, key, sortable = true }) => <th key={key}
              className={key === 'total' ? 'border-0 quote-amount' : 'border-0'}
              onClick={sortable ? () => { closeDetail(); sortBy(key); } : undefined} style={{ cursor: sortable ? 'pointer' : undefined }}
              aria-sort={sortable ? (query.sort.key === key ? (query.sort.direction === 'asc' ? 'ascending' : 'descending') : 'none') : undefined}>
              {label}{sortArrow(key)}
            </th>)}
          </tr></thead>
          <tbody>{visibleRows.map((quote, index) => <tr key={quote.id} onClick={() => { if (!busy) openQuote(quote); }}
            style={{ cursor: busy ? 'wait' : 'pointer' }} className={showDetail && selected?.id === quote.id ? 'table-active' : ''}>
            {canTransferQuotes && <td data-label="" onClick={event => event.stopPropagation()}>
              <input type="checkbox" aria-label={'Select quote ' + (quote.offer_no || quote.id)}
                checked={checkedIds.includes(quote.id)} onChange={event => toggleChecked(event, quote.id)} />
            </td>}
            <td data-label="#"><Card.Link href="#" className="text-primary fw-bold" onClick={event => event.preventDefault()}>
              {query.sort.key === 'index' && query.sort.direction === 'desc' ? pagination.total - offset - index : offset + index + 1}
            </Card.Link></td>
            {columns.map(({ label, key }) => <td key={key} data-label={label} className={key === 'total' ? 'quote-amount' : undefined}>
              {key === 'total' ? '\u20b9 ' + Number(quote.total || 0).toLocaleString('en-IN')
                : key === 'created_at' ? (quote.created_at?.slice(0, 10) || '—')
                  : key === 'owner.name' ? (quote.owner?.name || '—')
                  : nestedValue(quote, key)}
              {key === 'offer_no' && quote.has_po_sale && (
                <span className="quote-po-badge" title="This quote has a PO Sales record">PO Sales</span>
              )}
              {key === 'offer_no' && quote.was_transferred && (
                <span className="quote-transferred-badge" title={quote.transferred_from ? ('Transferred from ' + quote.transferred_from) : 'Transferred'}>
                  {quote.transferred_from ? ('Transferred from ' + quote.transferred_from) : 'Transferred'}
                </span>
              )}
            </td>)}
          </tr>)}</tbody>
        </Table>}
        {!compact && pagination.total > 0 && <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 p-3 border-top">
          <span className="text-muted small" role="status">
            Showing {rows.length ? offset + 1 : 0}-{offset + rows.length} of {pagination.total} quotes
          </span>
          {totalPages > 1 && <nav aria-label="Quotes pagination"><Pagination size="sm" className="mb-0 flex-wrap">
            <Pagination.Prev aria-label="Previous page" disabled={busy || currentPage === 1} onClick={() => changePage(currentPage - 1)} />
            {pageNumbers.map((page, index) => <React.Fragment key={page}>
              {index > 0 && page - pageNumbers[index - 1] > 1 && <Pagination.Ellipsis disabled />}
              <Pagination.Item active={page === currentPage} disabled={busy} aria-label={'Page ' + page}
                aria-current={page === currentPage ? 'page' : undefined} onClick={() => changePage(page)}>{page}</Pagination.Item>
            </React.Fragment>)}
            <Pagination.Next aria-label="Next page" disabled={busy || currentPage === totalPages} onClick={() => changePage(currentPage + 1)} />
          </Pagination></nav>}
        </div>}
      </Card.Body>
      <QuoteDetailSidebar quote={selected?.sessionToken === token ? detail.quote : null} show={showDetail && selected?.sessionToken === token} onClose={closeDetail}
        loading={pendingNavigation ? !error : detail.loading}
        error={pendingNavigation ? error : detail.error} onRetry={pendingNavigation ? listing.retry : detail.retry}
        onEdit={id => navigate('/edit/quote/' + id)} onDelete={handleDelete}
        onConvertToPoSales={id => navigate('/convert-po-sales/' + id)}
        onStatusSaved={() => {
          if (selected?.id) forgetQuoteDetail(token, selected.id);
          listing.retry();
          detail.retry();
        }}
        currentUserId={admin?.id}
        canManage={isVendorOwner(admin) || Boolean(selected?.created_by_id && String(selected.created_by_id) === String(admin?.id))}
        canEdit={hasUserPermission('quotes', 'edit', admin)} canDelete={hasUserPermission('quotes', 'delete', admin)}
        canConvert={hasUserPermission('po_sales', 'add', admin)}
        onPrevious={() => navigateQuote(-1)} onNext={() => navigateQuote(1)}
        hasPrevious={!busy && !pendingNavigation && selectedIndex >= 0 && (selectedIndex > 0 || currentPage > 1)}
        hasNext={!busy && !pendingNavigation && selectedIndex >= 0 && (selectedIndex < rows.length - 1 || currentPage < totalPages)} />
      <QuoteTransferModal
        show={showTransfer}
        onHide={() => setShowTransfer(false)}
        token={token}
        users={teammates}
        sourceUser={checkedQuotes[0] ? { id: checkedQuotes[0].created_by_id, name: checkedQuotes[0].owner?.name } : null}
        selectedQuotes={checkedQuotes}
        onTransferred={() => {
          setCheckedIds([]);
          listing.retry();
          clearQuoteDataCache();
        }}
      />
    </Card>
  );
}
